import { useMemo } from 'react'
import { Container } from '@carouselgg/nexus/container'
import { Section } from '@carouselgg/nexus/section'
import { Text } from '@carouselgg/nexus/text'
import { DisplayHeading } from '@carouselgg/nexus/display-heading'
import { useAuthStore } from '@carouselgg/auth/store'
import { eyebrow, greeting } from './greeting'
import { DashboardDataProvider } from './widgets/DashboardDataProvider'
import { useDashboardData } from './widgets/context'
import { orgPanels, todoGroups, weekCards } from './home/homeModel'
import { UpNext } from './home/UpNext'
import { WeekRow } from './home/WeekRow'
import { TodoByPlace } from './home/TodoByPlace'
import { OrgPanels } from './home/OrgPanels'
import { StartHere } from './home/StartHere'
import { YourCompetitions } from './home/YourCompetitions'
import { YourTeams } from './home/YourTeams'
import styles from './SignedInDashboard.module.css'

/**
 * The signed-in home. One page for every role.
 *
 * Tasks are merged across roles and labelled with where they come from; each
 * org the viewer runs keeps its own panel. Someone with nothing yet sees the
 * three ways in — sign up solo, start a team or club, run a league — and the
 * page fills in as they act.
 */
export function SignedInDashboard() {
  return (
    <DashboardDataProvider>
      <Board />
    </DashboardDataProvider>
  )
}

function Board() {
  const user = useAuthStore((s) => s.user)
  const { ctx, needsYou, upcoming, teams, standings, seasons, orgs, competitionGroups, reloadCompetitions, waitingByOrg, now, ready } = useDashboardData()

  const displayName = user?.gamer_tag || user?.username || 'there'

  const cards = useMemo(
    () => weekCards({ upcoming, teams, standings, seasons, orgs, now }),
    [upcoming, teams, standings, seasons, orgs, now],
  )
  // The first match you play gets the stage; the week row carries the rest.
  const next = cards.find((c): c is Extract<typeof c, { kind: 'match' }> => c.kind === 'match')
  const rest = cards.filter((c) => c !== next)
  const groups = useMemo(() => todoGroups(needsYou), [needsYou])
  const panels = useMemo(() => orgPanels(orgs, seasons, needsYou, waitingByOrg), [orgs, seasons, needsYou, waitingByOrg])

  // No team, no entry, no org, no fixture: show the three ways in instead of
  // a page of empty sections.
  const isNew = teams.length === 0 && orgs.length === 0 && competitionGroups.length === 0 && upcoming.length === 0
  const myTeam = next ? teams.find((t) => t.id === next.sides[0]?.teamID) : undefined

  return (
    // data-ready flips once every source has settled. Tests wait on it: a
    // negative assertion passes vacuously against a page that has not painted.
    <div className={styles.page} data-ready={ready ? 'true' : 'false'}>
      <Section spacing="lg">
        <Container size="xl">
          <div className={styles.stack}>
            <header className={styles.identity}>
              <Text size="xs" muted className={styles.eyebrow}>
                {eyebrow(now, ctx.personas)}
              </Text>
              <DisplayHeading as="h1" size="4xl" cut="soft" uppercase>
                {greeting(displayName, now)}
              </DisplayHeading>
            </header>

            {ready && isNew && <StartHere />}
            {next && <UpNext card={next} now={now} brandColor={myTeam?.primary_color} />}
            {!isNew && (next ? rest.length > 0 : true) && <WeekRow cards={rest} now={now} />}
            <TodoByPlace groups={groups} />
            <YourCompetitions groups={competitionGroups} standings={standings} onContactChanged={reloadCompetitions} />
            <YourTeams teams={teams} />
            <OrgPanels panels={panels} />
          </div>
        </Container>
      </Section>
    </div>
  )
}
